import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { NOTIFC, NOTIF_ICON } from "../../design/ui";
import { EmptyState, UserAvatar } from "../../components/mui";
import { fmtRelative } from "../../design/domain";
import { InlineLoading, Ring } from "../../components/loading";
import { MdClose, MdExpandMore, MdSend } from "react-icons/md";

/**
 * 右側滑出面板 — 對應 DESIGN 稿 L853-L873。
 * MUI Drawer anchor="right"，380px（手機 92vw）、含遮罩；panel = "notif" | "chat"。
 * 點遮罩、按 ESC 都走 onClose。
 *
 * 通知已經有自己的一頁（pages/Notify/Index.js，設計稿 #screen-notify）。
 * 抽屜這一版先原樣保留 —— 頁首的「通知」鈕要改成直接導頁還是照舊開抽屜，
 * 等兩邊都跑過一輪再決定，現在拔掉的話習慣用抽屜的人會先痛一次。
 * 兩邊讀的是同一份資料（hook/useNotification 的模組層狀態），在哪一邊標成已讀
 * 另一邊都會跟著變。底部多了一個進得去那一頁的入口。
 */
function RightPanel({
  panel,
  onClose,
  notifications,
  unreadCount,
  onMarkAllRead,
  onNotifClick,
  onLoadMoreNotif,
  chat,
  onLoadMoreChat,
  chatLoadingMore,
  chatHasMore,
  chatInput,
  onChatInput,
  onSendChat,
  chatConnected,
  onlineUsers,
}) {
  const navigate = useNavigate();
  const endRef = useRef(null);
  const chatScrollRef = useRef(null);
  /* 載入更舊訊息之前的捲動高度，用來把使用者的視線位置補回去 */
  const prevHeightRef = useRef(0);
  const [onlineOpen, setOnlineOpen] = useState(false);

  /* ---------- 聊天室的 @提及選單 ---------- */
  /*
   * 後端本來就吃 @：Chat/Hubs/ChatHub.cs 的 NotifyMentions 用
   * @([A-Za-z0-9_.\-]+) 從訊息內容抓「帳號」，抓到就寫一筆 mentioned 通知。
   * 少的一直是前端的選單 —— 打了 @ 沒有任何提示，等於要自己背得出對方的
   * 帳號全名（不是姓名），打錯一個字對方就收不到，而且畫面上看不出來打錯了。
   *
   * 名單只能從這兩個地方湊：線上清單（SignalR 推的 account+name）與這段
   * 對話裡出現過的人（訊息本身帶 account+name）。不打 /Auth/Admin/List ——
   * 那一支是 [PermissionOnly("admin")]，一般使用者會 403，選單會整個開不出來。
   * 因此名單不保證涵蓋全公司：沒上線也沒講過話的人不會出現在選單裡，
   * 但手動把帳號打完整一樣送得出去（後端認的是字串，不是這份選單）。
   */
  const mentionRef = useRef(null);
  const [mention, setMention] = useState(null); // { start, query, index } 或 null

  /* 線上的人優先排在前面：要 @ 的對象多半正在線上 */
  const mentionPool = React.useMemo(() => {
    const seen = new Map();
    (onlineUsers || []).forEach((u) => {
      if (u?.account && !seen.has(u.account)) seen.set(u.account, { ...u, online: true });
    });
    (chat || []).forEach((c) => {
      if (c?.account && !seen.has(c.account)) seen.set(c.account, { account: c.account, name: c.name, online: false });
    });
    return [...seen.values()];
  }, [onlineUsers, chat]);

  const mentionHits = React.useMemo(() => {
    if (!mention) return [];
    const q = mention.query.toLowerCase();
    return mentionPool
      .filter((u) => !q || u.account.toLowerCase().includes(q) || String(u.name || "").toLowerCase().includes(q))
      .slice(0, 6);
  }, [mention, mentionPool]);

  /*
   * 游標左邊那一段是不是正在打 @xxx。
   * 只認「@ 前面是開頭或空白」的情形，否則 email 之類的字串會一直把選單叫出來。
   */
  const syncMention = (value, caret) => {
    const left = value.slice(0, caret);
    const at = left.lastIndexOf("@");
    if (at < 0) return setMention(null);
    const before = at === 0 ? "" : left[at - 1];
    if (before && !/\s/.test(before)) return setMention(null);
    const query = left.slice(at + 1);
    /* 帳號不會有空白；打了空白就是這一段結束了，收起選單 */
    if (/\s/.test(query)) return setMention(null);
    setMention({ start: at, query, index: 0 });
  };

  const applyMention = (user) => {
    if (!mention || !user) return;
    const el = mentionRef.current;
    const caret = el ? el.selectionStart : chatInput.length;
    const next = `${chatInput.slice(0, mention.start)}@${user.account} ${chatInput.slice(caret)}`;
    onChatInput(next);
    setMention(null);
    /* 把游標移到補完的空白後面，讓人可以直接接著打字 */
    const pos = mention.start + user.account.length + 2;
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  useEffect(() => {
    if (panel !== "chat") return;

    /*
     * 有兩種情況要分開處理，不能一律捲到底：
     *   1. 剛開啟面板 / 有新訊息進來 → 捲到最下面（看最新的）
     *   2. 往上捲載入了更舊的訊息 → 內容從上面長出來，若不補正
     *      scrollTop，畫面會瞬間往下跳一大段，使用者正在讀的那一則會不見。
     */
    const el = chatScrollRef.current;
    if (prevHeightRef.current && el) {
      el.scrollTop += el.scrollHeight - prevHeightRef.current;
      prevHeightRef.current = 0;
      return;
    }
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
  }, [chat, panel]);

  if (!panel) return null;

  const title = panel === "notif" ? "通知中心" : "全體聊天室";

  return (
    <Drawer
      anchor="right"
      open={!!panel}
      onClose={onClose}
      slotProps={{ paper: { component: "aside", sx: { width: 380, maxWidth: "92vw", display: "flex", flexDirection: "column" } } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="h3" component="h2">
          {title}
        </Typography>
        {/*
          關閉鈕帶文字：純圖示叉叉沒有可及名稱，讀螢幕的人只會聽到一個沒有名字的按鈕（硬規則 3）。
          用 text 不用 contained：抽屜是疊在頁面上的浮層，頁面自己已經有主要鈕了。
        */}
        <Button type="button" variant="text" onClick={onClose} startIcon={<MdClose aria-hidden="true" />}>
          關閉
        </Button>
      </Box>

      {panel === "notif" && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2.5,
              py: 1,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="body2" component="span" color="text.secondary" sx={{ fontWeight: 600 }}>
              {unreadCount} 則未讀
            </Typography>
            <Button type="button" variant="text" onClick={onMarkAllRead}>
              全部已讀
            </Button>
          </Box>
          <Box
            sx={{ flex: 1, overflow: "auto" }}
            onScroll={(e) => {
              const el = e.target;
              if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50 && onLoadMoreNotif) {
                onLoadMoreNotif();
              }
            }}
          >
            {notifications.length === 0 ? (
              <EmptyState dense text="暫無通知" />
            ) : (
              <List disablePadding>
                {notifications.map((n, i) => {
                  const unread = !n.is_read;
                  const type = n.type || n.notify_type || "status_change";
                  return (
                    <ListItemButton
                      key={n.id || i}
                      divider
                      onClick={() => onNotifClick(n)}
                      sx={{
                        gap: 1.5,
                        px: 2.5,
                        py: 1.75,
                        alignItems: "flex-start",
                        ...(unread ? { bgcolor: "primary.light", "&:hover": { bgcolor: "action.selected" } } : {}),
                      }}
                    >
                      {/* 退路也要是 token：NOTIFC 沒收錄的類型（留言、審核通過／退回、快到期）
                          不能畫出一塊跟全站無關的字面色 */}
                      <Avatar
                        variant="rounded"
                        sx={{
                          width: 36,
                          height: 36,
                          fontSize: "var(--t-meta)",
                          fontWeight: 800,
                          color: "#fff",
                          bgcolor: NOTIFC[type] || "var(--progress-ink)",
                        }}
                      >
                        {NOTIF_ICON[type] || "知"}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700 }}>{n.title || "通知"}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                          {n.description || n.body || ""}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.disabled", fontWeight: 600, mt: 0.5 }}>
                          {fmtRelative(n.create_at || n.createAt)}
                        </Typography>
                      </Box>
                      {unread && (
                        <Box
                          component="span"
                          sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "error.main", flexShrink: 0, mt: 0.75 }}
                        />
                      )}
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>

          {/* 進通知頁的入口 */}
          <Box sx={{ px: 2.5, py: 1.75, borderTop: 1, borderColor: "divider", flexShrink: 0 }}>
            <Button
              type="button"
              variant="outlined"
              fullWidth
              onClick={() => {
                navigate("/Notifications");
                onClose();
              }}
            >
              檢視全部通知
            </Button>
          </Box>
        </>
      )}

      {panel === "chat" && (
        <>
          {/* 在線名單：預設收合只顯示人數，展開列出每個人（避免佔掉訊息區高度） */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
            <ListItemButton
              onClick={() => setOnlineOpen((v) => !v)}
              aria-expanded={onlineOpen}
              sx={{ gap: 1, px: 2.5, py: 1.25 }}
            >
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  flexShrink: 0,
                  bgcolor: chatConnected ? "success.main" : "warning.main",
                }}
              />
              <Typography
                variant="body2"
                component="span"
                color="text.secondary"
                sx={{ fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 0.75 }}
              >
                {/* 還在連線就在字前面轉一顆小圈，表示正在嘗試，不是卡住 */}
                {!chatConnected && <Ring size={0.9} />}
                {chatConnected ? `${onlineUsers.length} 位在線` : "連線中…"}
              </Typography>
              <Box sx={{ flex: 1 }} />
              {/* 收合時把前 4 位的頭像疊排出來，一眼看得到有誰 */}
              {!onlineOpen && (
                <Box component="span" sx={{ display: "flex", alignItems: "center" }}>
                  {onlineUsers.slice(0, 4).map((u, i) => (
                    <UserAvatar
                      key={u.account || i}
                      name={u.name}
                      title={u.name}
                      sx={{
                        width: 22,
                        height: 22,
                        fontSize: "calc(var(--t-meta) * 0.6)",
                        boxShadow: (t) => `0 0 0 2px ${t.palette.background.paper}`,
                        ml: i ? "-6px" : 0,
                      }}
                    />
                  ))}
                  {onlineUsers.length > 4 && (
                    <Typography variant="body2" component="span" sx={{ color: "text.disabled", fontWeight: 700, ml: 0.75 }}>
                      +{onlineUsers.length - 4}
                    </Typography>
                  )}
                </Box>
              )}
              <Box
                component="span"
                sx={{
                  display: "flex",
                  ml: 1,
                  flexShrink: 0,
                  color: "text.secondary",
                  transform: onlineOpen ? "rotate(180deg)" : "none",
                  transition: "transform .18s",
                }}
              >
                <MdExpandMore aria-hidden="true" />
              </Box>
            </ListItemButton>

            <Collapse in={onlineOpen} unmountOnExit>
              <Box sx={{ maxHeight: 184, overflow: "auto", px: 1, pb: 1 }}>
                {onlineUsers.length === 0 ? (
                  <Typography variant="body2" sx={{ px: 1.5, pt: 1.5, pb: 1.75, color: "text.disabled", fontWeight: 600 }}>
                    目前無其他成員在線
                  </Typography>
                ) : (
                  onlineUsers.map((u, i) => (
                    <Box
                      key={u.account || i}
                      sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.5, py: 0.875, borderRadius: "var(--radius-sm)" }}
                    >
                      <UserAvatar name={u.name} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography noWrap sx={{ fontWeight: 700 }}>
                          {u.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.disabled", fontWeight: 600 }}>
                          @{u.account}
                        </Typography>
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.625,
                          fontSize: "var(--t-meta)",
                          color: "success.main",
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "success.main", display: "inline-block" }}
                        />
                        線上
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Collapse>
          </Box>

          <Box
            ref={chatScrollRef}
            sx={{ flex: 1, overflow: "auto", px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 2 }}
            onScroll={(e) => {
              /*
               * 聊天的分頁方向與通知相反：舊訊息在「上面」，
               * 所以是捲到頂才載入，不是捲到底。
               *
               * 載入前先記住目前的 scrollHeight，新訊息插到前面之後
               * 再把 scrollTop 補回差值 —— 否則畫面會瞬間往下跳一大段，
               * 使用者正在讀的那一則會不見。
               */
              const el = e.target;
              if (el.scrollTop <= 40 && chatHasMore && !chatLoadingMore && onLoadMoreChat) {
                prevHeightRef.current = el.scrollHeight;
                onLoadMoreChat();
              }
            }}
          >
            {chatLoadingMore && (
              <Box sx={{ py: 1, display: "flex", justifyContent: "center", fontWeight: 600 }}>
                <InlineLoading text="載入更早的訊息…" />
              </Box>
            )}
            {!chatHasMore && chat.length > 0 && (
              <Typography variant="body2" sx={{ py: 1, textAlign: "center", color: "text.disabled" }}>
                已顯示至最早的訊息
              </Typography>
            )}
            {chat.length === 0 ? (
              <EmptyState dense text="尚無訊息" />
            ) : (
              chat.map((c, i) => (
                <Box key={c.id || i} sx={{ display: "flex", gap: 1.25 }}>
                  <UserAvatar name={c.name} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700 }}>
                      {c.name}{" "}
                      <Typography variant="body2" component="span" sx={{ color: "text.disabled", fontWeight: 600 }}>
                        {c.time}
                      </Typography>
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.25, overflowWrap: "anywhere" }}>
                      {c.is_recalled ? (
                        <Box component="i" sx={{ color: "text.disabled" }}>
                          訊息已收回
                        </Box>
                      ) : (
                        c.content || c.text
                      )}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
            <div ref={endRef} />
          </Box>
          <Box sx={{ px: 2, py: 1.75, borderTop: 1, borderColor: "divider", display: "flex", gap: 1, position: "relative" }}>
            {/* @提及選單。浮在輸入框上方而不是下方：抽屜已經貼著視窗底部，
                往下開會被切掉。 */}
            {mention && mentionHits.length > 0 ? (
              <Paper
                elevation={6}
                role="listbox"
                aria-label="選擇提及對象"
                sx={{ position: "absolute", left: 16, right: 16, bottom: "100%", mb: 0.75, zIndex: 2, overflow: "hidden" }}
              >
                {mentionHits.map((u, i) => (
                  <ListItemButton
                    key={u.account}
                    role="option"
                    aria-selected={i === mention.index}
                    selected={i === mention.index}
                    /* onMouseDown 而不是 onClick：onClick 之前輸入框會先失焦，
                       游標位置就跟著沒了，補字會補到錯的地方 */
                    onMouseDown={(e) => {
                      e.preventDefault();
                      applyMention(u);
                    }}
                    onMouseEnter={() => setMention((m) => (m ? { ...m, index: i } : m))}
                    sx={{ gap: 1.25, minHeight: 44, px: 1.5, py: 1 }}
                  >
                    <UserAvatar name={u.name} />
                    <Box component="span" sx={{ minWidth: 0, flex: 1 }}>
                      <Typography component="span" sx={{ display: "block", fontWeight: 700 }}>
                        {u.name}
                      </Typography>
                      <Typography variant="body2" component="span" sx={{ display: "block", color: "text.disabled", fontWeight: 600 }}>
                        @{u.account}
                      </Typography>
                    </Box>
                    {u.online ? (
                      <Typography variant="body2" component="span" sx={{ color: "success.main", fontWeight: 700, flexShrink: 0 }}>
                        線上
                      </Typography>
                    ) : null}
                  </ListItemButton>
                ))}
              </Paper>
            ) : null}
            <TextField
              inputRef={mentionRef}
              value={chatInput}
              sx={{ flex: 1 }}
              onChange={(e) => {
                onChatInput(e.target.value);
                syncMention(e.target.value, e.target.selectionStart);
              }}
              /* 用方向鍵或點一下換了游標位置，也要重算現在是不是在打 @ */
              onClick={(e) => syncMention(chatInput, e.target.selectionStart)}
              onBlur={() => setMention(null)}
              onKeyDown={(e) => {
                /* 選單開著的時候，上下鍵與 Enter 屬於選單，不屬於送出 */
                if (mention && mentionHits.length > 0) {
                  if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                    e.preventDefault();
                    const d = e.key === "ArrowDown" ? 1 : -1;
                    setMention((m) => ({
                      ...m,
                      index: (m.index + d + mentionHits.length) % mentionHits.length,
                    }));
                    return;
                  }
                  if (e.key === "Enter" || e.key === "Tab") {
                    e.preventDefault();
                    applyMention(mentionHits[mention.index]);
                    return;
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setMention(null);
                    return;
                  }
                }
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onSendChat();
                }
              }}
              placeholder={chatConnected ? "輸入訊息…（輸入 @ 可提及成員）" : "連線中…"}
            />
            {/*
              送出鈕帶文字：純圖示紙飛機沒有可及名稱，讀螢幕的人只聽得到一顆空按鈕。
              用 outlined 不用 contained：抽屜底下那一頁通常已經有一顆主要鈕（硬規則 1）。
            */}
            <Button type="button" variant="outlined" onClick={onSendChat} startIcon={<MdSend aria-hidden="true" />}>
              送出
            </Button>
          </Box>
        </>
      )}
    </Drawer>
  );
}

RightPanel.propTypes = {
  panel: PropTypes.string,
  onClose: PropTypes.func,
  notifications: PropTypes.array,
  unreadCount: PropTypes.number,
  onMarkAllRead: PropTypes.func,
  onNotifClick: PropTypes.func,
  onLoadMoreNotif: PropTypes.func,
  chat: PropTypes.array,
  onLoadMoreChat: PropTypes.func,
  chatLoadingMore: PropTypes.bool,
  chatHasMore: PropTypes.bool,
  chatInput: PropTypes.string,
  onChatInput: PropTypes.func,
  onSendChat: PropTypes.func,
  chatConnected: PropTypes.bool,
  onlineUsers: PropTypes.array,
};

RightPanel.defaultProps = {
  panel: null,
  onClose: () => {},
  notifications: [],
  unreadCount: 0,
  onMarkAllRead: () => {},
  onNotifClick: () => {},
  onLoadMoreNotif: null,
  chat: [],
  onLoadMoreChat: null,
  chatLoadingMore: false,
  chatHasMore: false,
  chatInput: "",
  onChatInput: () => {},
  onSendChat: () => {},
  chatConnected: true,
  onlineUsers: [],
};

export default RightPanel;
